import { TreeItem, TreeView } from "@mui/lab";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { observer } from "mobx-react-lite";
import { useStore } from "../../stores/store";
import React, { useEffect, useRef, useState } from "react";
import { Alert, CircularProgress, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Box } from "@mui/system";
import { AssetEquipment } from "../../models/asset";
import useHeight from "../../hook/useHeight";

type TSystemTreeSelector = {
  isRbd?: boolean;
};

function SystemTreeSelector({ isRbd = false }: TSystemTreeSelector) {
  const { assetStore, commonStore, rbdStore } = useStore();
  const { systems, loadingEquipment, elementsHeight } = assetStore;
  const { card: c, other: s, etc } = elementsHeight;
  const { selectedStation } = commonStore;
  const [expanded, setExpanded] = useState<string[]>([]);
  const [displayed, setDisplayed] = useState<AssetEquipment[]>([]);
  const [currentStationId, setCurrentStationId] = useState<number>(-1);
  const [searchValue, setSearchValue] = useState("");
  const [toggleValue, setToggleValue] = useState<"all" | "rbd">("all");
  const { ref: searchRef, height: searchHeight } = useHeight();
  const [isTermMatch, setIsTermMatch] = useState(true);
  const treeRef = useRef(null);
  const [selected, setSelected] = useState<string>("");

  const handleExpand = (nodeId: string) => {
    if (!expanded.includes(nodeId)) {
      setExpanded((prev) => [...prev, nodeId]);
    } else {
      setExpanded((prev) => prev.filter((i) => i !== nodeId && !i.startsWith(`${nodeId}.`)));
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setExpanded([String(currentStationId)]);
    setSearchValue(e.target.value.toLowerCase());
  };

  const handleTermNotMatch = (data: AssetEquipment[]) => {
    const isIncludeTrue = data.map((item) => item.id !== -1);
    setIsTermMatch(isIncludeTrue.includes(true));
  };

  const handleSelectedEquipment = (id: number) => {
    const prev = selected
      .split(".")
      .filter((i, x) => x === 0 || x === 1)
      .join(".");
    setSelected(`${prev}.${id}`);
    assetStore.setAssetSetting({ type: "equipment", id });
  };

  const handleSelectedSystem = (id: number) => {
    setSelected(`${selectedStation?.id}.${id}`);
    assetStore.setAssetSetting({ type: "system", id });
    rbdStore.getSystemDiagram(id);
  };
  const handleSelectedStation = (id: number) => {
    setSelected(`${id}`);
    assetStore.setAssetSetting({ type: "station", id });
    rbdStore.getStationDiagram(id);
  };

  const handleDoubleclickSystemTree = (systemNodeId: string, id: number) => {
    setExpanded((prev) => [...prev.filter((i, x) => x === 0), systemNodeId]);
    handleExpand(systemNodeId);
  };

  const handleChangeToggle = (event: React.MouseEvent<HTMLElement>, newAlignment: "all" | "rbd") => {
    if (newAlignment !== null) {
      setToggleValue(newAlignment);
    }
  };

  useEffect(() => {
    if (currentStationId !== -1) {
      assetStore.getEquipments(String(currentStationId));
    }
  }, [assetStore, currentStationId]);

  useEffect(() => {
    let term = searchValue;
    const filteredByToggleAndPage = !isRbd ? systems : toggleValue === "all" ? systems : systems.filter((i) => i.hasDiagram);

    const filterSystem = filteredByToggleAndPage.filter((system) => system.name.toLowerCase().includes(term));
    const filterEquipment = filteredByToggleAndPage.map((system) => {
      const equipments = system.equipments.filter((equipment) => {
        return equipment.name.toLowerCase().includes(term);
      });
      return equipments.length > 0 ? { ...system, equipments } : { name: "", id: -1, equipments: [], hasDiagram: false };
    });

    if (term.length > 0) {
      filterEquipment.filter((e) => e.id !== -1).map(({ id }) => setExpanded((prev) => [...prev, `${currentStationId}.${id}`]));
    }

    const result = filterSystem.length > 0 ? filterSystem : filterEquipment;

    handleTermNotMatch(result);
    setDisplayed(result);
  }, [currentStationId, isRbd, searchValue, systems, toggleValue]);

  useEffect(() => {
    setDisplayed(systems);
  }, [systems]);

  useEffect(() => {
    if (selectedStation) {
      setCurrentStationId(selectedStation.id);
      setExpanded([String(selectedStation?.id)]);
    }
    return () => setCurrentStationId(-1);
  }, [selectedStation]);

  useEffect(() => {
    assetStore.setOtherHeight(searchHeight);
  }, [searchHeight, assetStore]);

  useEffect(() => {
    if (!assetStore.assetSetting.id) return;
    if (!selectedStation) return;

    if (assetStore.assetSetting.type === "station") {
      setSelected(`${selectedStation.id}`);
      return;
    }
    if (assetStore.assetSetting.type === "system") {
      setSelected(`${selectedStation.id}.${assetStore.assetSetting.id}`);
    }
  }, [setSelected, assetStore, selectedStation]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {isRbd && (
        <ToggleButtonGroup sx={{ marginBottom: "12px" }} fullWidth color="primary" value={toggleValue} exclusive onChange={handleChangeToggle}>
          <ToggleButton value="all">All System</ToggleButton>
          <ToggleButton value="rbd">System with RBD</ToggleButton>
        </ToggleButtonGroup>
      )}
      <Box ref={searchRef} sx={{ display: "flex", mb: 2, gap: 1 }}>
        <TextField
          sx={{ flex: 1 }}
          id="search-system-equipment"
          size="small"
          variant="outlined"
          placeholder="Search..."
          onChange={(e) => handleSearch(e)}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          overflow: loadingEquipment ? "hidden" : "auto",
          maxHeight: c - s - etc - 15,
        }}
      >
        {loadingEquipment ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {displayed.length > 0 && isTermMatch ? (
              <TreeView
                ref={treeRef}
                aria-label="file system navigator"
                selected={selected}
                expanded={expanded}
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
                sx={{ flexGrow: 1, overflowY: "auto" }}
              >
                <TreeItem
                  onClick={() => handleSelectedStation(currentStationId)}
                  nodeId={String(currentStationId)}
                  label={selectedStation ? selectedStation.name : ""}
                >
                  {displayed.map((system, systemIdx) => {
                    let systemNodeId = `${currentStationId}.${system.id}`;

                    return (
                      <TreeItem
                        onClick={() => handleSelectedSystem(system.id)}
                        onDoubleClick={() => handleDoubleclickSystemTree(systemNodeId, system.id)}
                        key={systemIdx}
                        nodeId={systemNodeId}
                        label={system.name}
                      >
                        {system.equipments.map((equipment, equipmentIdx) => {
                          let equipmentNodeId = `${systemNodeId}.${equipment.id}`;

                          return (
                            <TreeItem
                              onClick={() => handleSelectedEquipment(equipment.id)}
                              key={equipmentIdx}
                              nodeId={equipmentNodeId}
                              label={equipment.name}
                            />
                          );
                        })}
                      </TreeItem>
                    );
                  })}
                </TreeItem>
              </TreeView>
            ) : (
              <Alert severity="info">Data not found</Alert>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default observer(SystemTreeSelector);
